<script lang="ts" setup>
import { computed } from "vue";

import { CLASS_NAME_COMPONENT, createBlockClasses, createContentClasses } from "./common";
import {
  color as colorProp,
  fullWidth as fullWidthProp,
  size as sizeProp,
  sizeLg as sizeLgProp,
  sizeMd as sizeMdProp,
  sizeSm as sizeSmProp,
  sizeXl as sizeXlProp,
  sizeXxl as sizeXxlProp,
  state as stateProp,
  tag as tagProp,
  variant as variantProp
} from "./props";

const props = defineProps({
  color: colorProp,
  fullWidth: fullWidthProp,
  size: sizeProp,
  sizeSm: sizeSmProp,
  sizeMd: sizeMdProp,
  sizeLg: sizeLgProp,
  sizeXl: sizeXlProp,
  sizeXxl: sizeXxlProp,
  state: stateProp,
  tag: tagProp,
  variant: variantProp
});

const attributes = computed(() => ({
  size: props.size,
  sizeSm: props.sizeSm,
  sizeMd: props.sizeMd,
  sizeLg: props.sizeLg,
  sizeXl: props.sizeXl,
  sizeXxl: props.sizeXxl,
  color: props.color,
  state: props.state
}));

const classObject = computed(() => [
  CLASS_NAME_COMPONENT,
  props.fullWidth ? `${CLASS_NAME_COMPONENT}--full-width` : ""
]);

const componentVariantClasses = computed(() =>
  createBlockClasses(`${CLASS_NAME_COMPONENT}-${props.variant}`, attributes.value)
);

const contentVariantClasses = computed(() =>
  createContentClasses(`${CLASS_NAME_COMPONENT}__content`, attributes.value)
);
</script>

<template>
  <Component v-bind="$attrs" :is="props.tag" :class="[classObject, componentVariantClasses]">
    <span :class="contentVariantClasses">
      <slot></slot>
    </span>
  </Component>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@disabled-color: rgb(@theme-colors[@grey][50]);

.tg-button {
  display: inline-block;
  transition: all 100ms ease-in-out;

  &:focus {
    outline: none;
  }

  &:hover {
    text-decoration: none;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &--full-width {
    width: 100%;
    text-align: center;
  }

  &__content {
    transition: all 100ms ease-in-out;
    font-weight: 600;
    letter-spacing: 1px;
    text-align: center;
    text-transform: uppercase;
    word-break: break-word;
    hyphens: auto;

    each(@grid-breakpoints, {
      @name: @key;

      &--size-@{name} {
        &-sm {
          #media-breakpoint-up(@name, {
            font-size: 0.875rem;
            line-height: 1.125rem;
          });
        }

        &-md {
          #media-breakpoint-up(@name, {
            font-size: 1rem;
            line-height: 1.2rem;
          });
        }

        &-lg {
          #media-breakpoint-up(@name, {
            font-size: 1.125rem;
            line-height: 1.375rem;
          });
        }
      }
    });
  }
}

.tg-button-solid {
  border: none;

  &:disabled {
    border-color: @disabled-color;
    background-color: @disabled-color;
    box-shadow: none;
  }

  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--@{name} {
      @bg-color: rgb(@value[base]);
      @bg-color-dark: rgb(@value[110]);
      @font-color: @colors[@white];
      @shadow-color: rgba(@value[base], 0.2);
      @shadow-color-dark: rgba(@value[base], 0.3);

      border: 2px solid @bg-color; // To align the height with the outline variant
      background-color: @bg-color;
      box-shadow: 5px 5px 10px 0 @shadow-color;
      color: @font-color;

      &:hover:not(:disabled):not(:focus):not(.tg-button-solid--@{name}-focused),
      &-hovered:not(:disabled):not(:focus):not(.tg-button-solid--@{name}-focused) {
        border-color: @bg-color-dark;
        background-color: @bg-color-dark;
        box-shadow: 5px 5px 10px 0 @shadow-color, 0 0 0 1px @bg-color-dark;
        color: @font-color;
      }

      &:focus,
      &-focused {
        border-color: @bg-color;
        background-color: @bg-color;
        color: @font-color;
      }

      &-sm:focus,
      &-sm-focused {
        box-shadow: 5px 5px 10px 0 @shadow-color, 0 0 0 3px @shadow-color-dark;
      }

      &-md:focus,
      &-md-focused {
        box-shadow: 5px 5px 10px 0 @shadow-color, 0 0 0 4px @shadow-color-dark;
      }

      &-lg:focus,
      &-lg-focused {
        box-shadow: 5px 5px 10px 0 @shadow-color, 0 0 0 5px @shadow-color-dark;
      }
    }
  });

  each(@grid-breakpoints, {
    @name: @key;

    &--size-@{name} {
      &-sm {
        #media-breakpoint-up(@name, {
          padding: 4px 16px;
          border-radius: 20px;
        });
      }

      &-md {
        #media-breakpoint-up(@name, {
          padding: 9px 34px;
          border-radius: 22px;
        });
      }

      &-lg {
        #media-breakpoint-up(@name, {
          padding: 13px 42px;
          border-radius: 32px;
        });
      }
    }
  });
}

.tg-button-outline {
  border: none;

  &:disabled {
    border-color: @disabled-color;
    box-shadow: none;
    color: @disabled-color;
  }

  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--@{name} {
      @bg-color: transparent;
      @border-color: rgb(@value[base]);
      @border-color-dark: rgb(@value[110]);
      @shadow-color: rgba(@value[base], 0.3);

      border: 2px solid @border-color;
      background-color: @bg-color;
      color: @border-color;

      &:hover:not(:disabled):not(:focus):not(.tg-button-outline--@{name}-focused),
      &-hovered:not(:disabled):not(:focus):not(.tg-button-outline--@{name}-focused) {
        border: 2px solid @border-color-dark;
        background-color: @bg-color;
        box-shadow: 0 0 0 1px @border-color-dark;
        color: @border-color-dark;
      }

      &:focus,
      &-focused {
        border: 2px solid @border-color;
        background-color: @bg-color;
        color: @border-color;
      }

      &-sm:focus,
      &-sm-focused {
        box-shadow: 0 0 0 3px @shadow-color;
      }

      &-md:focus,
      &-md-focused {
        box-shadow: 0 0 0 4px @shadow-color;
      }

      &-lg:focus,
      &-lg-focused {
        box-shadow: 0 0 0 5px @shadow-color;
      }
    }
  });

  each(@grid-breakpoints, {
    @name: @key;

    &--size-@{name} {
      &-sm {
        #media-breakpoint-up(@name, {
          padding: 4px 16px;
          border-radius: 20px;
        });
      }

      &-md {
        #media-breakpoint-up(@name, {
          padding: 9px 34px;
          border-radius: 22px;
        });
      }

      &-lg {
        #media-breakpoint-up(@name, {
          padding: 13px 42px;
          border-radius: 32px;
        });
      }
    }
  });
}

.tg-button-text {
  position: relative;
  transition:
    background 150ms ease-in-out,
    color 150ms ease-in-out,
    box-shadow 150ms ease-in-out;
  border: none;
  border-radius: 4px;
  background: transparent;

  &:disabled {
    color: @disabled-color;
  }

  &::after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    right: -9px;
    bottom: 0;
    left: -9px;
    transition: border-color 150ms ease-in-out;
    border: 2px solid transparent;
    border-radius: 4px;
  }

  .color-theme-mixin() {
    color: @font-color;

    &:hover:not(:disabled):not(:focus):not(.tg-button-text--@{name}-focused),
    &-hovered:not(:disabled):not(:focus):not(.tg-button-text--@{name}-focused) {
      color: @font-color-dark;
    }

    &:focus,
    &-focused {
      background: @bg-color;
      box-shadow:
        -9px 0px 0px 0 @bg-color,
        8px 0px 0px 0 @bg-color;
      color: @font-color;

      &::after {
        border-color: @font-color;
      }
    }
  }

  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--@{name} {
      @font-color: rgb(@value[base]);
      @font-color-dark: rgb(@value[110]);
      @bg-color: rgba(@value[base], 0.1);

      .color-theme-mixin();
    }
  });

  &--white {
    @name: white;
    @font-color: white;
    @font-color-dark: white;
    @bg-color: rgba(255, 255, 255, 0.1);

    .color-theme-mixin();
  }

  each(@grid-breakpoints, {
    @name: @key;

    &--size-@{name} {
      &-sm {
        #media-breakpoint-up(@name, {
          padding: 3px 0;
        });
      }

      &-md {
        #media-breakpoint-up(@name, {
          padding: 5px 0;
        });
      }

      &-lg {
        #media-breakpoint-up(@name, {
          padding: 7px 0;
        });
      }
    }
  });
}
</style>
